// Europe/Tallinn wall-clock helpers based on Intl, so charts and labels show local house time
// regardless of the browser's own time zone (Highcharts' `time.timezone` would need moment-timezone).

export const TALLINN_TZ = 'Europe/Tallinn';

const partsFormatter = new Intl.DateTimeFormat('en-GB', {
  timeZone: TALLINN_TZ,
  hourCycle: 'h23',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  weekday: 'short',
});

export interface TallinnParts {
  year: number;
  month: number; // 1-12
  day: number;
  hour: number;
  minute: number;
  second: number;
  weekday: string; // Mon, Tue, ...
}

export function tallinnParts(ts: number): TallinnParts {
  const parts: Record<string, string> = {};
  for (const p of partsFormatter.formatToParts(new Date(ts))) {
    parts[p.type] = p.value;
  }
  return {
    year: Number(parts.year),
    month: Number(parts.month),
    day: Number(parts.day),
    hour: Number(parts.hour) % 24,
    minute: Number(parts.minute),
    second: Number(parts.second),
    weekday: parts.weekday,
  };
}

// Same sign convention as Date.prototype.getTimezoneOffset / Highcharts `time.getTimezoneOffset`:
// minutes to add to local time to get UTC (Tallinn summer time => -180).
export function tallinnTimezoneOffset(ts: number): number {
  const p = tallinnParts(ts);
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  const tsSeconds = Math.floor(ts / 1000) * 1000;
  return Math.round((tsSeconds - asUtc) / 60_000);
}

// The Tallinn offset only changes on whole UTC hours, so cache it per hour (Highcharts calls this a lot).
const offsetCache = new Map<number, number>();
export function tallinnOffsetCached(ts: number): number {
  const key = Math.floor(ts / 3_600_000);
  let offset = offsetCache.get(key);
  if (offset === undefined) {
    offset = tallinnTimezoneOffset(key * 3_600_000);
    offsetCache.set(key, offset);
  }
  return offset;
}

export const localMinuteOfDay = (ts: number) => {
  const m = Math.round((ts - tallinnOffsetCached(ts) * 60_000) / 60_000);
  return ((m % 1440) + 1440) % 1440;
};

// The last local midnight (start of today in Tallinn).
export function localMidnight(ts: number): number {
  const p = tallinnParts(ts);
  return ts - ((p.hour * 60 + p.minute) * 60 + p.second) * 1000 - (ts % 1000);
}

const pad = (n: number) => (n < 10 ? `0${n}` : String(n));

export function formatTallinnTime(ts: number): string {
  const p = tallinnParts(ts);
  return `${pad(p.hour)}:${pad(p.minute)}`;
}

export function formatTallinnDayTime(ts: number): string {
  const p = tallinnParts(ts);
  return `${p.weekday} ${pad(p.day)}.${pad(p.month)} ${pad(p.hour)}:${pad(p.minute)}`;
}

export function formatRelative(ts: number, now: number): string {
  const diffMin = Math.round((now - ts) / 60_000);
  if (diffMin < 1) {
    return 'just now';
  }
  if (diffMin < 60) {
    return `${diffMin} min ago`;
  }
  const hours = Math.floor(diffMin / 60);
  if (hours < 48) {
    return `${hours} h ${diffMin % 60} min ago`;
  }
  return `${Math.floor(hours / 24)} days ago`;
}
